import React from "react";
import { motion } from "framer-motion";
import { Calendar, Clock, MapPin, Users, ArrowUpRight } from "lucide-react";
import { UPCOMING_EVENTS, PAST_EVENTS } from "../lib/data";

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.8, ease: [0.16, 1, 0.3, 1] },
};

const Markets = () => (
  <div data-testid="markets-page">
    <section className="px-6 md:px-12 lg:px-16 pt-24 md:pt-32 pb-16">
      <motion.div {...fadeUp} className="max-w-5xl">
        <p className="tracking-overline text-xs text-[#9A7456] mb-6">
          Upcoming Markets
        </p>
        <h1
          className="font-display text-5xl md:text-7xl lg:text-8xl leading-[0.98]"
          data-testid="markets-title"
        >
          Where we'll be next.
        </h1>
        <p className="font-body text-lg text-[#231F1E]/75 mt-8 max-w-2xl leading-relaxed">
          Dates are being confirmed — stay tuned. RSVP below to be the first to
          know when we announce.
        </p>
      </motion.div>
    </section>

    {/* Event list — editorial cards */}
    <section className="px-6 md:px-12 lg:px-16 pb-24 md:pb-32 space-y-24 md:space-y-32">
      {UPCOMING_EVENTS.map((e, idx) => (
        <motion.article
          key={e.id}
          {...fadeUp}
          className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-16"
          data-testid={`event-card-${e.id}`}
        >
          <div className={`lg:col-span-7 ${idx % 2 ? "lg:order-2" : ""}`}>
            <div className="aspect-[5/4] media-zoom">
              <img
                src={e.cover}
                alt={e.name}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          <div className={`lg:col-span-5 ${idx % 2 ? "lg:order-1" : ""} lg:py-8`}>
            <p className="tracking-overline text-[10px] text-[#9A7456] mb-4">
              {String(idx + 1).padStart(2, "0")} · {e.status}
            </p>
            <h2 className="font-display text-4xl md:text-5xl leading-[1.05]">
              {e.name}
            </h2>
            <p className="font-body text-base text-[#231F1E]/75 mt-5 leading-relaxed">
              {e.description}
            </p>
            <div className="mt-8 space-y-3 text-sm">
              <div className="flex items-center gap-3 text-[#231F1E]">
                <Calendar size={14} className="text-[#9A7456]" />
                <span>{e.date}</span>
              </div>
              {e.time && e.time !== "Time TBD" && (
                <div className="flex items-center gap-3 text-[#231F1E]">
                  <Clock size={14} className="text-[#9A7456]" />
                  <span>{e.time}</span>
                </div>
              )}
              <div className="flex items-center gap-3 text-[#231F1E]">
                <MapPin size={14} className="text-[#9A7456]" />
                <span>
                  {e.location}
                  {e.address && <span className="text-[#231F1E]/50"> · {e.address}</span>}
                </span>
              </div>
              {e.vendorCount && e.vendorCount !== "TBA" && (
                <div className="flex items-center gap-3 text-[#231F1E]">
                  <Users size={14} className="text-[#9A7456]" />
                  <span>{e.vendorCount}</span>
                </div>
              )}
            </div>

            {e.highlights.length > 0 && (
              <ul className="mt-8 grid grid-cols-2 gap-2 text-xs text-[#231F1E]/85">
                {e.highlights.map((h, i) => (
                  <li key={i} className="flex items-start gap-2 leading-relaxed">
                    <span className="text-[#D6B66B] mt-1">✦</span> {h}
                  </li>
                ))}
              </ul>
            )}

            <div className="mt-10 flex flex-wrap gap-3">
              <a
                href={e.rsvpUrl}
                className="btn-primary"
                data-testid={`rsvp-${e.id}`}
              >
                RSVP — Shopper
              </a>
              <a
                href="https://forms.gle/k5X22jtCcHQh2fZi6"
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary"
                data-testid={`apply-${e.id}`}
              >
                Apply as Vendor
              </a>
            </div>
          </div>
        </motion.article>
      ))}
    </section>

    {/* Past events */}
    <section className="bg-[#9A7456] text-[#EDE6DA] py-24 md:py-32 px-6 md:px-12 lg:px-16">
      <p className="tracking-overline text-xs text-[#EDE6DA]/70 mb-6">
        Past Editions
      </p>
      <h2 className="font-display text-4xl md:text-5xl leading-tight mb-12 max-w-2xl">
        A look back at recent markets.
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-10">
        {PAST_EVENTS.map((p) => (
          <a
            key={p.id}
            href={p.pixiesetUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="group block"
            data-testid={`past-event-${p.id}`}
          >
            <div className="aspect-[16/10] media-zoom mb-5">
              <img src={p.cover} alt={p.name} className="w-full h-full object-cover" />
            </div>
            <div className="flex items-end justify-between">
              <div>
                <h3 className="font-display text-3xl">{p.name}</h3>
                <p className="text-xs tracking-overline text-[#EDE6DA]/70 mt-2">
                  {p.date} · {p.location}
                </p>
              </div>
              <span className="inline-flex items-center gap-2 text-xs tracking-overline text-[#D6B66B] link-underline">
                Full gallery <ArrowUpRight size={14} />
              </span>
            </div>
          </a>
        ))}
      </div>
    </section>
  </div>
);

export default Markets;
